import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Observable, of, Subject, takeUntil } from 'rxjs';

import {
  BrandsEnum, BrandThemesEnum, ComponentWrapperWidthEnum, IComponentWrapper, MarginEnum, OrientationEnum,
  PositionEnum, SdComponentWrapperComponent, SizesEnum, ThemesEnum
} from '@seb/web-design-system/core';

import { CustomMedia, Wrapper } from '../../../../models';
import { UppercaseBrandThemesEnum } from '../../../../enums';
import { HeadingParagraphMapper, LinkMapper, ThemesMapper, toFootnote } from '../../../../mappers';
import { ChatService, ImageService, TrackingService, HobStateService, BaseStoreService } from '../../../../services';

@Component({
  selector: 'wc-wrapper',
  templateUrl: './wrapper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SdComponentWrapperComponent]
})
export class WrapperComponent implements OnChanges, OnDestroy {
  content: IComponentWrapper;

  themesConfig: ThemesEnum | BrandThemesEnum;

  brand: BrandsEnum;
  isHob = false;

  layout: OrientationEnum.row | OrientationEnum.column = OrientationEnum.column;
  textConfig: PositionEnum.center | PositionEnum.left | PositionEnum.right = PositionEnum.center;
  layoutTextConfig: PositionEnum.left | PositionEnum.right | PositionEnum.center = PositionEnum.center;

  @Input()
  size: SizesEnum.l | SizesEnum.m | SizesEnum.s = SizesEnum.l;

  @Input()
  marginSize: MarginEnum.default_margin | MarginEnum.without_margin;

  @Input()
  componentWidth: ComponentWrapperWidthEnum = ComponentWrapperWidthEnum.default;

  @Input()
  contentWrapper: Wrapper;

  @Input()
  isUnderlined: boolean;

  @Input()
  isPushCard = false;

  @Input()
  titleTheme;

  @Input()
  activeBrand: BrandsEnum;

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private imageService: ImageService,
    private linkMapper: LinkMapper,
    private trackingService: TrackingService,
    private chatService: ChatService,
    private cdRef: ChangeDetectorRef,
    private headingParagraphMapper: HeadingParagraphMapper,
    private readonly hobStateService: HobStateService,
    private readonly baseStoreService: BaseStoreService
  ) {
    this.hobStateService.isHob$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isHob) => this.isHob = isHob);
  }

  ngOnChanges(): void {
    if (this.contentWrapper) {
      const brand = this.baseStoreService.getBrand(this.contentWrapper.brandCorner);
      let isBrandLogo = false;
      if (brand) {
        this.brand = this.contentWrapper.brandCorner;
        isBrandLogo = this.isPushCard;
      }

      this.themesConfig = ThemesMapper.mapBrandTheme(
        this.contentWrapper?.wrapperBackgroundTheme as UppercaseBrandThemesEnum
      );
      this.layoutTextConfig =
        (this.contentWrapper?.wrapperPosition?.toLowerCase() as
          | PositionEnum.left
          | PositionEnum.right
          | PositionEnum.center) || PositionEnum.center;
      this.textConfig =
        (this.contentWrapper?.alignment?.toLowerCase() as
          | PositionEnum.left
          | PositionEnum.right
          | PositionEnum.center) || PositionEnum.center;

      this.marginSize = this.marginSize ?? this.contentWrapper?.wrapperMargin?.toLowerCase() as MarginEnum.default_margin | MarginEnum.without_margin;

      this.layout =
        (this.contentWrapper?.layouts?.toLowerCase() as OrientationEnum.row | OrientationEnum.column) ||
        OrientationEnum.column;

      const mediaContainer$: Observable<CustomMedia> = this.contentWrapper?.wrapperBackgroundImage
        ? this.imageService.getMediaFromContainer(this.contentWrapper?.wrapperBackgroundImage)
        : of(undefined);

      mediaContainer$.pipe(takeUntil(this.destroy$)).subscribe((media: CustomMedia) => {
        const headingData = {
          heading: {
            headline: this.contentWrapper?.wrapperHeading?.headline,
            subline: this.contentWrapper?.wrapperHeading?.subline,
            description: this.contentWrapper?.wrapperHeading?.description,
            media: !isBrandLogo && this.contentWrapper?.wrapperHeading?.media,
            pictos: this.contentWrapper?.wrapperHeading?.pictos,
            headlineLevel: this.contentWrapper?.wrapperHeading?.headlineLevel
          },
          brandLogo: isBrandLogo
            ? { media: this.imageService.normalizeMedia(this.contentWrapper?.wrapperHeading?.media) }
            : undefined,
          theme: this.contentWrapper?.wrapperHeading?.themesEnum?.toLowerCase(),
          isUnderlined: this.contentWrapper?.wrapperHeading?.isUnderlined,
          size: this.contentWrapper?.wrapperHeading?.size
            ? (this.contentWrapper?.wrapperHeading?.size?.toLowerCase() as
              | SizesEnum.xl
              | SizesEnum.l
              | SizesEnum.m
              | SizesEnum.s)
            : SizesEnum.l,
          textConfig: this.contentWrapper?.wrapperHeading?.textConfig,
          isDescription: this.contentWrapper?.wrapperHeading?.isDescription,
          titleLevel: this.contentWrapper?.wrapperHeading?.headlineLevel
        };

        this.content = {
          heading: this.headingParagraphMapper.toHeadingParagraph(headingData),
          footNotes: toFootnote(this.contentWrapper),
          links: this.contentWrapper?.wrapperButton
            ? [this.linkMapper.toLinkMedia(this.contentWrapper?.wrapperButton)]
            : []
        };

        if (media) {
          this.content = { ...this.content, background: this.imageService.normalizeMedia(media) };
        }
        this.cdRef.markForCheck();
      });
    }
  }
  onLinkClicked(event) {
    if (event?.link?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
    if (event.isBrandCorner) {
      const eventType = this.isHob ? 'clickCrossBrand' : 'clickBrandCorner';
      const positionLabel = this.isHob ? 'section tell me more' : 'section';
      this.trackingService.sendEvent(eventType, {
        productCategory: event.category,
        positionLabel: positionLabel
      });
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
